import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { REQUEST_PASSWORD_RESET_MUTATION } from "@/graphql/auth";

const forgotPasswordSchema = z.object({
  email: z.string().email("auth:forgotPassword.errors.emailInvalid"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

interface UseForgotPasswordFormOptions {
  onSuccess?: () => void;
}

export function useForgotPasswordForm({ onSuccess }: UseForgotPasswordFormOptions = {}) {
  const [requestPasswordReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET_MUTATION);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = async (data: ForgotPasswordFormData) => {
    try {
      const result = await requestPasswordReset({
        variables: {
          input: {
            email: data.email,
          },
        },
      });

      if (result.data?.requestPasswordReset.success) {
        onSuccess?.();
      }
    } catch (error) {
      // Security: Always show success message to prevent email enumeration
      // The backend error is logged but not shown to user
      console.error("Password reset request error:", error);
      onSuccess?.();
    }
  };

  return {
    form,
    isLoading: loading,
    onSubmit: form.handleSubmit(handleSubmit),
  };
}
